import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    X,
    Download,
    Upload,
    FileSpreadsheet,
    CheckCircle,
    AlertCircle,
    Loader2,
    ChevronRight,
} from 'lucide-react';
import { apiClient, useAuthStore } from '@dukene/shared';

// ── CSV template columns & example ───────────────────────────────────────────
// Professional Header Mapping (Human Readable -> Internal Key)
const HEADER_MAP = {
    'Handle': 'handle',
    'Title': 'name',
    'Short Description': 'shortDescription',
    'Description': 'description',
    'SKU': 'sku',
    'Price': 'price',
    'Compare At Price': 'compareAtPrice',
    'Cost Per Item': 'costPerItem',
    'Category': 'categoryName',
    'Track Inventory': 'trackQuantity',
    'Inventory Quantity': 'quantity',
    'Low Stock Threshold': 'lowStockThreshold',
    'Status': 'status',
    'Tags': 'tags',
    'Featured': 'featured',
    'SEO Title': 'metaTitle',
    'SEO Description': 'metaDescription',
    'SEO Keywords': 'metaKeywords',
    'Affiliate Enabled': 'affiliateEnabled',
    'Commission Type': 'commissionType',
    'Commission Value': 'commissionValue',
    'Option 1 Name': 'variant_option1_name',
    'Option 1 Value': 'variant_option1_value',
    'Option 2 Name': 'variant_option2_name',
    'Option 2 Value': 'variant_option2_value',
    'Variant SKU': 'variant_sku',
    'Variant Price': 'variant_price',
    'Variant Compare At Price': 'variant_compareAtPrice',
    'Variant Quantity': 'variant_quantity',
};

const TEMPLATE_HEADERS = Object.keys(HEADER_MAP);

// Realistic product generator for the example file
function generateExampleRows() {
    const products: string[][] = [];
    const addSimple = (h: string, n: string, d: string, p: string, cat: string, tags: string) => {
        // Fill all product fields + variant placeholders
        products.push([
            h, n, n, d, `SKU-${h.toUpperCase()}`, p, (parseInt(p) * 1.2).toString(), (parseInt(p) * 0.7).toString(),
            cat, 'TRUE', '50', '5', 'ACTIVE', tags, 'FALSE',
            '', '', '', 'FALSE', 'PERCENTAGE', '10', // SEO + Affiliate
            '', '', '', '', '', '', '', '' // Variant fields
        ]);
    };

    addSimple('ankara-tote-bag', 'Ankara Tote Bag', 'Handcrafted premium tote bag', '1800', 'Bags', 'ankara, bag');
    addSimple('leather-wallet-pro', 'Classic Leather Wallet', 'Genuine cowhide bi-fold wallet', '3500', 'Accessories', 'leather, wallet');
    addSimple('ultra-comfort-sneakers', 'Pro Run Sneakers', 'Breathable mesh running shoes', '6800', 'Footwear', 'shoes, running');
    addSimple('minimalist-desk-lamp', 'Minimalist LED Lamp', 'Touch-controlled dimmable lamp', '2200', 'Home Office', 'home, electronics');

    // Add 100 variations of products in a loop for realism
    const categories = ['Electronics', 'Fashion', 'Home Decor', 'Beauty', 'Kitchen'];
    for (let i = 1; i <= 96; i++) {
        const isVariant = i % 10 === 0;
        const cat = categories[i % categories.length];
        const handle = `prod-item-${i}`;
        const title = `${cat} Item #${i}`;
        if (isVariant) {
            // Add a variant product (2 sizes)
            products.push([
                handle, title, `Best selling ${title}`, `Description for ${title}`, `SKU-${i}-S`, '2000', '2500', '1400',
                cat, 'TRUE', '', '5', 'ACTIVE', 'bulk, imported', 'FALSE',
                '', '', '', 'FALSE', 'PERCENTAGE', '10',
                'Size', 'Small', '', '', `SKU-${i}-S`, '2000', '2500', '10'
            ]);
            products.push([
                handle, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '',
                'Size', 'Large', '', '', `SKU-${i}-L`, '2400', '3000', '15'
            ]);
        } else {
            products.push([
                handle, title, `Professional ${title}`, `Full details for ${title}`, `SKU-${i}`,
                (1200 + (i * 10)).toString(), (1500 + (i * 10)).toString(), (800 + (i * 10)).toString(),
                cat, 'TRUE', '100', '10', 'ACTIVE', 'bulk', 'FALSE',
                '', '', '', 'FALSE', 'PERCENTAGE', '10',
                '', '', '', '', '', '', '', ''
            ]);
        }
    }
    return products;
}

const TEMPLATE_EXAMPLE_ROWS = generateExampleRows();

async function downloadTemplate() {
    const JSZipModule = (await import('jszip')).default;
    const zip = new JSZipModule();

    // ── 1. instructions.html ────────────────────────────────────────────────────
    const instructions = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Dukene Bulk Product Import — Instructions</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 860px; margin: 40px auto; padding: 0 24px; color: #111; line-height: 1.65; }
  h1 { font-size: 28px; font-weight: 800; border-bottom: 3px solid #10b981; padding-bottom: 12px; margin-bottom: 8px; }
  h2 { font-size: 18px; font-weight: 700; margin-top: 36px; color: #10b981; }
  h3 { font-size: 14px; font-weight: 700; margin-top: 20px; margin-bottom: 4px; }
  p, li { font-size: 14px; color: #333; }
  code { background: #f0fdf4; color: #065f46; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 12px; }
  th { background: #10b981; color: #fff; padding: 8px 12px; text-align: left; }
  td { padding: 7px 12px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  tr:nth-child(even) td { background: #f9fafb; }
  .badge { display: inline-block; background: #d1fae5; color: #065f46; padding: 1px 8px; border-radius: 99px; font-size: 11px; font-weight: 700; }
  .warn { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 10px 14px; border-radius: 0 6px 6px 0; margin: 12px 0; font-size: 13px; }
  .tip  { background: #f0fdf4; border-left: 4px solid #10b981; padding: 10px 14px; border-radius: 0 6px 6px 0; margin: 12px 0; font-size: 13px; }
  ol, ul { padding-left: 20px; } li { margin-bottom: 4px; }
  @media print { body { margin: 20px; } }
</style>
</head>
<body>
<h1>Dukene — Bulk Product Import Guide</h1>
<p>Use the two CSV files in this package to import your products into Dukene in bulk.<br/>
After importing, you can add product images and fine-tune details from the <strong>Edit</strong> page.</p>

<h2>Quick Start</h2>
<ol>
  <li>Open <code>template.csv</code> in Excel or Google Sheets.</li>
  <li>Fill in your products — one row per product (or one row per variant for variable products).</li>
  <li>Save the file as <strong>CSV (UTF-8)</strong>.</li>
  <li>In Dukene, go to <strong>Products → Import</strong> and upload your CSV file.</li>
  <li>Review the import preview, then click <strong>Import</strong>.</li>
</ol>

<div class="tip">ℹ️ See <code>example.csv</code> for a fully filled reference with one simple product and one product with variants.</div>

<h2>File Columns</h2>
<table>
  <thead><tr><th>Column</th><th>Required?</th><th>Description &amp; Rules</th></tr></thead>
  <tbody>
    <tr><td><code>handle</code></td><td><span class="badge" style="background:#fef3c7;color:#92400e">Semi-Required</span></td><td>Unique URL slug. Letters, numbers, hyphens only.<br/><em>*Auto-generated from Title if left blank.</em></td></tr>
    <tr><td><code>name</code></td><td><span class="badge">Required</span></td><td>Product display name. Fill on the <strong>first row</strong> per handle.</td></tr>
    <tr><td><code>shortDescription</code></td><td>Optional</td><td>Brief one-line summary.</td></tr>
    <tr><td><code>description</code></td><td><span class="badge">Required</span></td><td>Full product description. Plain text.</td></tr>
    <tr><td><code>sku</code></td><td><span class="badge">Required</span></td><td>Unique SKU. Auto-generated if left blank but highly recommended.</td></tr>
    <tr><td><code>price</code></td><td><span class="badge">Required</span></td><td>Base price (number). For variants, use <code>Variant Price</code>.</td></tr>
    <tr><td><code>compareAtPrice</code></td><td>Optional</td><td>Sale price (crossed out).</td></tr>
    <tr><td><code>costPerItem</code></td><td>Optional</td><td>Wholesale cost (internal only).</td></tr>
    <tr><td><code>categoryName</code></td><td>Optional</td><td>Category name to match existing ones.</td></tr>
    <tr><td><code>trackQuantity</code></td><td>Optional</td><td><code>TRUE</code> or <code>FALSE</code>.</td></tr>
    <tr><td><code>quantity</code></td><td>Optional</td><td>Stock for simple products.</td></tr>
    <tr><td><code>lowStockThreshold</code></td><td>Optional</td><td>Default: <code>10</code>.</td></tr>
    <tr><td><code>status</code></td><td>Optional</td><td><code>DRAFT</code>, <code>ACTIVE</code>, <code>ARCHIVED</code>.</td></tr>
    <tr><td><code>tags</code></td><td>Optional</td><td>Comma-separated.</td></tr>
    <tr><td><code>featured</code></td><td>Optional</td><td><code>TRUE</code> or <code>FALSE</code>.</td></tr>
    <tr><td colspan="3" style="background:#f3f4f6;font-weight:700">SEO &amp; Affiliate</td></tr>
    <tr><td><code>metaTitle</code></td><td>Optional</td><td>SEO Title tag.</td></tr>
    <tr><td><code>metaDescription</code></td><td>Optional</td><td>SEO Meta description.</td></tr>
    <tr><td><code>metaKeywords</code></td><td>Optional</td><td>SEO Keywords.</td></tr>
    <tr><td><code>affiliateEnabled</code></td><td>Optional</td><td><code>TRUE</code> or <code>FALSE</code>.</td></tr>
    <tr><td><code>commissionType</code></td><td>Optional</td><td><code>PERCENTAGE</code> or <code>FIXED</code>.</td></tr>
    <tr><td><code>commissionValue</code></td><td>Optional</td><td>Commission amount.</td></tr>
    <tr><td colspan="3" style="background:#f3f4f6;font-weight:700">Variants</td></tr>
    <tr><td><code>variant_option1_name</code></td><td>Variants only</td><td>e.g. <code>Size</code>.</td></tr>
    <tr><td><code>variant_option1_value</code></td><td>Variants only</td><td>e.g. <code>Small</code>.</td></tr>
    <tr><td><code>variant_option2_name</code></td><td>Variants only</td><td>e.g. <code>Color</code>.</td></tr>
    <tr><td><code>variant_option2_value</code></td><td>Variants only</td><td>e.g. <code>Red</code>.</td></tr>
    <tr><td><code>variant_sku</code></td><td>Variants only</td><td>SKU for this variant.</td></tr>
    <tr><td><code>variant_price</code></td><td>Variants only</td><td>Price for this variant.</td></tr>
    <tr><td><code>variant_compareAtPrice</code></td><td>Variants only</td><td>Sale price for variant.</td></tr>
    <tr><td><code>variant_quantity</code></td><td>Variants only</td><td>Stock for this variant.</td></tr>
  </tbody>
</table>

<h2>Variant Products</h2>
<p>To import a product with variants (e.g. Size &times; Color), use <strong>one row per combination</strong> 
   and repeat the same <code>handle</code> on every row.</p>
<div class="warn">️ Only fill in product-level fields (<code>name</code>, <code>description</code>, <code>status</code>, <code>tags</code>, etc.) on the <strong>first row</strong> of each handle. Leave them blank on subsequent variant rows.</div>

<h3>Example — Polo Shirt with 4 variants:</h3>
<p>Handle: <code>dukene-polo-shirt</code> repeated on all 4 rows.<br/>Size (Small, Large) &times; Color (Red, Blue) = 4 combinations.</p>

<h2>Common Mistakes</h2>
<ul>
  <li>Sending the same <code>handle</code> for two <em>different</em> products — they will be merged as variants.</li>
  <li>Using spaces or special characters in <code>handle</code>. Use hyphens only: <code>my-product-name</code>.</li>
  <li>Importing a product whose name or handle already exists in your store — Dukene will reject duplicates.</li>
  <li>Leaving <code>price</code> blank for simple products (it will default to 0).</li>
  <li>Mixing commas inside tag values without quoting the cell in Excel.</li>
</ul>

<h2>After Import</h2>
<p>All products are imported as <strong>DRAFT</strong> unless you set <code>status</code> to <code>ACTIVE</code>.
You can add product images and adjust details from the <strong>Products</strong> page by clicking the edit (✏️) button on any product.</p>

<p style="margin-top:48px;font-size:12px;color:#9ca3af">© ${new Date().getFullYear()} Dukene — Bulk Import Guide</p>
</body></html>`;

    // ── 2. template.csv (clean — headers + one blank row) ───────────────────────
    const templateHeaders = TEMPLATE_HEADERS.join(',');
    const blankRow = TEMPLATE_HEADERS.map(() => '').join(',');
    const templateCsv = [templateHeaders, blankRow].join('\n');

    // ── 3. example.csv (fully filled) ───────────────────────────────────────────
    const exampleCsv = [
        TEMPLATE_HEADERS.join(','),
        ...TEMPLATE_EXAMPLE_ROWS.map(r => r.map(v => v ? `"${v}"` : '').join(',')),
    ].join('\n');

    // ── Build & download the ZIP ─────────────────────────────────────────────────
    zip.file('instructions.html', instructions);
    zip.file('template.csv', templateCsv);
    zip.file('example.csv', exampleCsv);

    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dukene_import_kit.zip';
    a.click();
    URL.revokeObjectURL(url);
}

function parseCSV(text: string): Record<string, string>[] {
    // Strip comment lines (starting with #)
    const lines = text.split('\n').filter(l => !l.trim().startsWith('#') && l.trim());
    if (lines.length < 2) return [];

    const rawHeaders = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    // Map professional headers back to internal keys (ignore case/extra spaces)
    const headers = rawHeaders.map(rh => {
        const match = Object.entries(HEADER_MAP).find(([k]) => k.toLowerCase() === rh.toLowerCase());
        return match ? match[1] : rh;
    });

    return lines.slice(1).map(line => {
        const values: string[] = [];
        let inQ = false, cur = '';
        for (const ch of line) {
            if (ch === '"') { inQ = !inQ; }
            else if (ch === ',' && !inQ) { values.push(cur.trim()); cur = ''; }
            else { cur += ch; }
        }
        values.push(cur.trim());
        const row: Record<string, string> = {};
        headers.forEach((h, i) => {
            const val = (values[i] ?? '').replace(/"/g, '').trim();
            row[h] = val;
        });
        return row;
    }).filter(r => r.name || r.handle);
}

// Slugify: convert product name to URL handle (matches backend logic)
function slugify(text: string): string {
    return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

// Group flat CSV rows into products by handle (same handle = variant rows of one product)
function groupRowsIntoProducts(rows: Record<string, string>[]) {
    const map = new Map<string, Record<string, string>[]>();
    const order: string[] = [];
    rows.forEach(row => {
        // Use handle if provided, otherwise auto-generate from name
        const key = (row.handle || slugify(row.name));
        if (!map.has(key)) { map.set(key, []); order.push(key); }
        map.get(key)!.push(row);
    });
    return order.map(handle => ({ handle, rows: map.get(handle)! }));
}

type ImportStep = 'template' | 'upload' | 'preview' | 'done';
interface ImportResult { success: number; failed: number; errors: string[]; }

interface ProductImportModalProps {
    onClose: () => void;
    onDone: () => void;
}

export default function ProductImportModal({ onClose, onDone }: ProductImportModalProps) {
    const [step, setStep] = useState<ImportStep>('template');
    const [rows, setRows] = useState<Record<string, string>[]>([]);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState<ImportResult | null>(null);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const activeStoreId = useAuthStore((state) => state.activeStoreId);

    // Fetch categories to resolve categoryName -> categoryId
    const { data: categories } = useQuery({
        queryKey: ['categories', activeStoreId],
        queryFn: () => apiClient.get<any[]>('/products/categories'),
        enabled: !!activeStoreId,
    });

    const handleFile = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const parsed = parseCSV(text);
            setRows(parsed);
            setStep('preview');
        };
        reader.readAsText(file);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    };

    const runImport = async () => {
        setImporting(true);
        setStep('done');
        const res: ImportResult = { success: 0, failed: 0, errors: [] };
        const products = groupRowsIntoProducts(rows);
        for (let i = 0; i < products.length; i++) {
            const { handle, rows: pRows } = products[i];
            const first = pRows[0];
            const productTitle = first.name;
            const productHandle = first.handle || handle; // use CSV handle or auto-generated one
            const hasVariants = pRows.some(
                r => r.variant_option1_value || r.variant_option2_value
            );

            // Resolve Category ID from name
            const categoryId = categories?.find(
                c => c.name.toLowerCase() === first.categoryName?.toLowerCase()
            )?.id;

            try {
                // Determine affiliate status
                const affiliateEnabled = first.affiliateEnabled?.toUpperCase() === 'TRUE';

                // Build base payload — backend uses 'title' not 'name'
                const base: Record<string, any> = {
                    title: productTitle,
                    handle: productHandle,
                    shortDescription: first.shortDescription || undefined,
                    description: first.description || undefined,
                    sku: first.sku || undefined,
                    price: parseFloat(first.price) || 0,
                    compareAtPrice: first.compareAtPrice ? parseFloat(first.compareAtPrice) : undefined,
                    costPerItem: first.costPerItem ? parseFloat(first.costPerItem) : undefined,
                    categoryId,
                    categories: categoryId ? [categoryId] : [],
                    status: ['ACTIVE', 'DRAFT', 'ARCHIVED'].includes(first.status?.toUpperCase())
                        ? first.status.toUpperCase() : 'DRAFT',
                    trackInventory: first.trackQuantity?.toUpperCase() === 'TRUE',
                    initialQuantity: first.quantity ? parseInt(first.quantity) : 0,
                    lowStockThreshold: first.lowStockThreshold ? parseInt(first.lowStockThreshold) : undefined,
                    featured: first.featured?.toUpperCase() === 'TRUE',
                    tags: first.tags ? first.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
                    // SEO
                    metaTitle: first.metaTitle || undefined,
                    metaDescription: first.metaDescription || undefined,
                    metaKeywords: first.metaKeywords || undefined,
                    // Affiliate
                    affiliateEnabled,
                    commissionType: affiliateEnabled ? (['PERCENTAGE', 'FIXED'].includes(first.commissionType?.toUpperCase())
                        ? first.commissionType.toUpperCase() : 'PERCENTAGE') : undefined,
                    commissionValue: affiliateEnabled && first.commissionValue ? parseFloat(first.commissionValue) : undefined,
                };

                if (hasVariants) {
                    // Collect unique option names
                    const opt1Name = pRows.find(r => r.variant_option1_name)?.variant_option1_name || '';
                    const opt2Name = pRows.find(r => r.variant_option2_name)?.variant_option2_name || '';
                    // Build attributes
                    const attrMap: Record<string, Set<string>> = {};
                    if (opt1Name) attrMap[opt1Name] = new Set();
                    if (opt2Name) attrMap[opt2Name] = new Set();
                    pRows.forEach(r => {
                        if (opt1Name && r.variant_option1_value) attrMap[opt1Name].add(r.variant_option1_value);
                        if (opt2Name && r.variant_option2_value) attrMap[opt2Name].add(r.variant_option2_value);
                    });
                    const attributes = Object.entries(attrMap).map(([n, vals]) => ({
                        name: n, type: 'BUTTON', values: [...vals],
                    }));
                    // Build variant combinations
                    const variants = pRows.map(r => {
                        const attrValuesList: { attributeName: string; value: string }[] = [];
                        if (opt1Name && r.variant_option1_value)
                            attrValuesList.push({ attributeName: opt1Name, value: r.variant_option1_value });
                        if (opt2Name && r.variant_option2_value)
                            attrValuesList.push({ attributeName: opt2Name, value: r.variant_option2_value });
                        const title = attrValuesList.map(av => av.value).join(' / ');
                        return {
                            title,
                            sku: r.variant_sku || '',
                            price: parseFloat(r.variant_price || first.price) || 0,
                            compareAtPrice: r.variant_compareAtPrice ? parseFloat(r.variant_compareAtPrice) : undefined,
                            quantity: parseInt(r.variant_quantity || first.quantity || '0') || 0,
                            available: true,
                            attributeValues: attrValuesList, // ✅ array format as backend expects
                        };
                    });

                    // Calculate total quantity from variants
                    base.initialQuantity = variants.reduce((sum, v) => sum + (v.quantity || 0), 0);

                    await apiClient.post('/products', { ...base, hasVariants: true, attributes, variants });
                } else {
                    await apiClient.post('/products', base);
                }
                res.success++;
            } catch {
                res.failed++;
                res.errors.push(`"${productTitle}" (${productHandle}) — failed to import`);
            }
            setProgress(Math.round(((i + 1) / products.length) * 100));
        }
        setResult(res);
        setImporting(false);
        if (res.success > 0) onDone();
    };

    const STEPS: { key: ImportStep; label: string }[] = [
        { key: 'template', label: 'Template' },
        { key: 'upload', label: 'Upload' },
        { key: 'preview', label: 'Review' },
        { key: 'done', label: 'Done' },
    ];
    const stepIndex = STEPS.findIndex(s => s.key === step);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald/10 flex items-center justify-center">
                            <FileSpreadsheet className="w-4 h-4 text-emerald" />
                        </div>
                        <h2 className="font-bold text-gray-900">Bulk Import Products</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                        <X className="w-4 h-4 text-gray-500" />
                    </button>
                </div>

                {/* Step indicator */}
                <div className="px-6 py-3 bg-gray-50 border-b flex items-center gap-1">
                    {STEPS.map((s, i) => (
                        <div key={s.label} className="flex items-center gap-1">
                            <div key={s.key} className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${i === stepIndex ? 'bg-emerald text-white' :
                                i < stepIndex ? 'bg-emerald/15 text-emerald' : 'text-gray-400'
                                }`}>
                                {i < stepIndex ? <CheckCircle className="w-3 h-3" /> : <span>{i + 1}</span>}
                                {s.label}
                            </div>
                            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />}
                        </div>
                    ))}
                </div>

                {/* Body */}
                <div className="p-6">

                    {/* Step 1 — Template */}
                    {step === 'template' && (
                        <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
                                <p className="font-semibold mb-1">How it works</p>
                                <ol className="list-decimal list-inside space-y-1 text-blue-700">
                                    <li>Download the import kit (ZIP) below</li>
                                    <li>Open <strong>instructions.html</strong> for the full guide</li>
                                    <li>Fill in <strong>template.csv</strong> with your products</li>
                                    <li>Upload the CSV here — products are created instantly</li>
                                    <li>Add images & details from the edit page after import</li>
                                </ol>
                            </div>
                            <div className="border rounded-lg p-4 flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                                    <FileSpreadsheet className="w-5 h-5 text-emerald" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-semibold text-gray-900 text-sm">dukene_import_kit.zip</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Contains instructions.html · template.csv · example.csv</p>
                                </div>
                                <button
                                    onClick={downloadTemplate}
                                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald text-white text-sm font-semibold hover:bg-emerald-dark transition-colors"
                                >
                                    <Download className="w-4 h-4" /> Download
                                </button>
                            </div>
                            <button
                                onClick={() => setStep('upload')}
                                className="w-full h-11 rounded-lg bg-gray-900 text-white font-semibold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                            >
                                I have a file ready <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {/* Step 2 — Upload */}
                    {step === 'upload' && (
                        <div className="space-y-4">
                            <div
                                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${dragOver ? 'border-emerald bg-emerald/5' : 'border-gray-200 hover:border-emerald/50 hover:bg-gray-50'
                                    }`}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleDrop}
                                onClick={() => fileRef.current?.click()}
                            >
                                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                                <p className="font-semibold text-gray-700">Drop your CSV file here</p>
                                <p className="text-sm text-gray-400 mt-1">or click to browse</p>
                                <p className="text-xs text-gray-300 mt-3">.csv files only · Max 5MB</p>
                            </div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".csv"
                                className="hidden"
                                onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
                            />
                            <button onClick={() => setStep('template')} className="text-sm text-gray-500 hover:text-gray-700">
                                ← Back to template
                            </button>
                        </div>
                    )}

                    {/* Step 3 — Preview */}
                    {step === 'preview' && (
                        <div className="space-y-4">
                            {(() => {
                                const grouped = groupRowsIntoProducts(rows);
                                const withVariants = grouped.filter(p =>
                                    p.rows.some(r => r.variant_option1_value || r.variant_option2_value)
                                );
                                return (
                                    <>
                                        <div className="bg-emerald/5 border border-emerald/20 rounded-lg p-4 flex items-center gap-3">
                                            <CheckCircle className="w-5 h-5 text-emerald shrink-0" />
                                            <div>
                                                <p className="font-semibold text-gray-900 text-sm">
                                                    {grouped.length} product{grouped.length !== 1 ? 's' : ''} ready to import
                                                    {withVariants.length > 0 && (
                                                        <span className="ml-2 text-xs font-normal text-gray-500">
                                                            ({withVariants.length} with variants)
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-0.5">All imported as <strong>DRAFT</strong> unless specified. Add images from the edit page after import.</p>
                                            </div>
                                        </div>

                                        {/* Preview table */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider grid grid-cols-3 gap-2">
                                                <span>Name</span><span>Variants</span><span>Base Price</span>
                                            </div>
                                            <div className="divide-y max-h-48 overflow-y-auto">
                                                {grouped.slice(0, 10).map((p, i) => {
                                                    const hasV = p.rows.some(r => r.variant_option1_value || r.variant_option2_value);
                                                    return (
                                                        <div key={i} className="px-3 py-2 text-sm grid grid-cols-3 gap-2 items-center">
                                                            <span className="font-medium text-gray-900 truncate">{p.rows[0].name || p.handle}</span>
                                                            <span>
                                                                {hasV
                                                                    ? <span className="bg-purple-50 text-purple-600 text-xs font-semibold px-2 py-0.5 rounded-full">{p.rows.length} variants</span>
                                                                    : <span className="text-gray-400 text-xs">Simple</span>
                                                                }
                                                            </span>
                                                            <span className="text-gray-700">KES {parseFloat(p.rows[0].price || p.rows[0].variant_price || '0').toLocaleString()}</span>
                                                        </div>
                                                    );
                                                })}
                                                {grouped.length > 10 && (
                                                    <div className="px-3 py-2 text-xs text-gray-400 text-center">+ {grouped.length - 10} more products</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-3">
                                            <button onClick={() => setStep('upload')} className="flex-1 h-11 rounded-lg border text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                                                Change File
                                            </button>
                                            <button
                                                onClick={runImport}
                                                className="flex-1 h-11 rounded-lg bg-emerald text-white font-semibold text-sm hover:bg-emerald-dark transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Upload className="w-4 h-4" /> Import {grouped.length} Products
                                            </button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {/* Step 4 — Done / Progress */}
                    {step === 'done' && (
                        <div className="space-y-5">
                            {importing ? (
                                <div className="text-center py-6">
                                    <Loader2 className="w-10 h-10 text-emerald animate-spin mx-auto mb-4" />
                                    <p className="font-semibold text-gray-900">Importing products…</p>
                                    <p className="text-sm text-gray-500 mt-1">{progress}% complete</p>
                                    <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-emerald rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                                    </div>
                                </div>
                            ) : result && (
                                <>
                                    <div className={`rounded-lg p-4 flex items-start gap-3 ${result.failed === 0 ? 'bg-emerald/5 border border-emerald/20' : 'bg-amber-50 border border-amber-200'
                                        }`}>
                                        {result.failed === 0
                                            ? <CheckCircle className="w-5 h-5 text-emerald shrink-0 mt-0.5" />
                                            : <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />}
                                        <div>
                                            <p className="font-semibold text-gray-900 text-sm">
                                                {result.success} product{result.success !== 1 ? 's' : ''} imported successfully
                                                {result.failed > 0 && `, ${result.failed} failed`}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-0.5">You can now add images and edit details from the Products page.</p>
                                        </div>
                                    </div>
                                    {result.errors.length > 0 && (
                                        <div className="border rounded-lg p-3 max-h-32 overflow-y-auto">
                                            {result.errors.map((e, i) => (
                                                <p key={i} className="text-xs text-red-600 py-0.5">{e}</p>
                                            ))}
                                        </div>
                                    )}
                                    <button
                                        onClick={onClose}
                                        className="w-full h-11 rounded-lg bg-gray-900 text-white font-semibold text-sm hover:bg-gray-800 transition-colors"
                                    >
                                        Close & View Products
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
